import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { type LocationId } from '@brightlocal/shared';

import type { PlaceDetails } from 'shared/components/GooglePlaceAutocomplete/types';
import { prefixKey } from 'shared/utils';

import type { BusinessDetails } from '../types';

export const GET_STARTED_STEPS = {
  UPDATE_BUSINESS_DETAILS: 'update-business-details',
  FIND_LOCATION: 'find-location',
  ADD_KEYWORDS: 'add-keywords',
  ADD_PROMPTS: 'add-prompts',
  GETTING_INSIGHTS: 'getting-insights'
} as const;

type GetStartedStep = (typeof GET_STARTED_STEPS)[keyof typeof GET_STARTED_STEPS];

interface GetStartedData {
  // Business details for the onboarding step
  businessDetails: BusinessDetails;
  // Details of the location user want to get insights for
  placeDetails: PlaceDetails;
  // The ID of the location the user wants to get insights for (created in database)
  locationId: LocationId | null;
  // Keywords the user wants to track
  keywords: string[];
  // Prompts the user wants to track
  prompts: string[];
  // Steps the user has completed
  completedSteps: GetStartedStep[];
}

interface GetStartedActions {
  // Set business details for the onboarding step
  setBusinessDetails: (businessDetails: BusinessDetails) => void;
  // Set the details of the location user want to get insights for
  setPlaceDetails: (placeDetails: PlaceDetails) => void;
  // Set the ID of the location the user wants to get insights for (created in database)
  setLocationId: (locationId: LocationId) => void;
  // Set the keywords the user wants to track
  setKeywords: (keywords: string[]) => void;
  // Set the prompts the user wants to track
  setPrompts: (prompts: string[]) => void;
  // Mark that the user has entered and completed the getting-insights screen
  setGettingInsightsEntered: VoidFunction;
  /**
   * Marks the remaining basic-flow steps complete when onboarding ends via an extended-country
   * (CB-only) location. Mirrors Simply Listings: find-location + CB is enough.
   */
  completeBasicOnboardingExtendedCountry: VoidFunction;
  // Reset the store to initial data
  reset: VoidFunction;
}

type GetStartedState = GetStartedData & GetStartedActions;

// This key is used to persist the get started flow data in local storage
const SIGN_UP_STORAGE_KEY = 'sign_up_get_started_flow';

const initialData: GetStartedData = {
  businessDetails: {
    fullName: '',
    name: '',
    websiteUrl: null,
    country: '',
    businessType: '',
    managedLocationRange: ''
  },
  placeDetails: {
    placeId: '',
    displayName: '',
    pureServiceAreaBusiness: false
  },
  locationId: null,
  keywords: [],
  prompts: [],
  completedSteps: []
};

const addCompletedStep = (completedSteps: GetStartedStep[], step: GetStartedStep): GetStartedStep[] =>
  completedSteps.includes(step) ? completedSteps : [...completedSteps, step];

const removeCompletedStep = (completedSteps: GetStartedStep[], step: GetStartedStep): GetStartedStep[] =>
  completedSteps.filter((s) => s !== step);

export const useGetStartedStore = create<GetStartedState>()(
  persist(
    immer((set) => ({
      ...initialData,
      setBusinessDetails: (businessDetails) =>
        set((state) => {
          state.businessDetails = businessDetails;
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.UPDATE_BUSINESS_DETAILS);
        }),
      setPlaceDetails: (placeDetails) =>
        set((state) => {
          const { placeId: previousPlaceId } = state.placeDetails;
          const isLocationChanged = !!previousPlaceId && previousPlaceId !== placeDetails.placeId;

          state.placeDetails = placeDetails;
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.FIND_LOCATION);

          if (isLocationChanged) {
            state.keywords = [];
            state.prompts = [];
            state.completedSteps = removeCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_KEYWORDS);
            state.completedSteps = removeCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_PROMPTS);
            state.completedSteps = removeCompletedStep(state.completedSteps, GET_STARTED_STEPS.GETTING_INSIGHTS);
          }
        }),
      setLocationId: (locationId) =>
        set((state) => {
          state.locationId = locationId;
        }),
      setKeywords: (keywords) =>
        set((state) => {
          state.keywords = keywords;
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_KEYWORDS);
        }),
      setPrompts: (prompts) =>
        set((state) => {
          state.prompts = prompts;
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_PROMPTS);
        }),
      setGettingInsightsEntered: () =>
        set((state) => {
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.GETTING_INSIGHTS);
        }),
      completeBasicOnboardingExtendedCountry: () =>
        set((state) => {
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_KEYWORDS);
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.ADD_PROMPTS);
          state.completedSteps = addCompletedStep(state.completedSteps, GET_STARTED_STEPS.GETTING_INSIGHTS);
        }),
      reset: () => set(initialData)
    })),
    { name: prefixKey(SIGN_UP_STORAGE_KEY) }
  )
);
